import { useTheme } from '@mui/material';
import Decimal from 'decimal.js';
import { useMemo } from 'react';

import { useChartColorDefinitions } from '@/components/charts/constants';
import { ClientDetailsGiftingPage_HouseholdFragment } from '@/modules/gifting/graphql/ClientDetailsGiftingPage.generated';
import { StateCode } from '@/types/schema';
import { sumDecimalJS } from '@/utils/decimalJSUtils';

import {
  createClampNegativeValuesToZero,
  getCTExemptionSections,
  getGSTExemptionSections,
  getLifetimeExemptionSections,
  ZERO_DECIMAL,
} from './useGiftingExemptionsForClient.utils';
import { useGSTExemptionAmount } from './useGSTExemptionAmount';
import { useLifetimeExemptionAmount } from './useLifetimeExemptionAmount';

export function useGiftingExemptionsForClient({
  household,
  returnNegativeValues = true,
  groupByGrantor = true,
}: {
  household?: ClientDetailsGiftingPage_HouseholdFragment;
  returnNegativeValues?: boolean;
  groupByGrantor?: boolean;
}) {
  const theme = useTheme();
  const colors = useChartColorDefinitions();
  const numGrantors = household?.possiblePrimaryClients.length ?? 0;
  const possiblePrimaryClients = useMemo(() => {
    return household?.possiblePrimaryClients ?? [];
  }, [household?.possiblePrimaryClients]);
  const clampNegativeValuesToZero = useMemo(() => {
    return createClampNegativeValuesToZero(returnNegativeValues);
  }, [returnNegativeValues]);

  const grantorIdToExemptionsMap: Record<
    string,
    {
      lifetimeExemptionAmount: Decimal;
      gstExemptionAmount: Decimal;
    }
  > = useMemo(() => ({}), []);

  const {
    forAllGrantors: lifetimeExemptionAmountForAllGrantors,
    perGrantor: lifetimeExemptionAmountPerGrantor,
  } = useLifetimeExemptionAmount({ numGrantors });

  const {
    forAllGrantors: gstExemptionAmountForAllGrantors,
    perGrantor: gstExemptionAmountPerGrantor,
  } = useGSTExemptionAmount({ numGrantors });

  // get the total of used lifetime exemption for all grantors
  const lifetimeExclusionUsedTotal = useMemo(() => {
    return possiblePrimaryClients.reduce((acc, grantor) => {
      return acc.plus(grantor.lifetimeExclusionUsed ?? 0);
    }, ZERO_DECIMAL);
  }, [possiblePrimaryClients]);

  // get the total of used GST exemption for all grantors
  const gstExclusionUsedTotal = useMemo(() => {
    return possiblePrimaryClients.reduce((acc, grantor) => {
      return acc.plus(grantor.gstExclusionUsed ?? 0);
    }, ZERO_DECIMAL);
  }, [possiblePrimaryClients]);

  // get the total of used CT exemption for CT grantors only
  const ctExemptionUsedTotal = useMemo(() => {
    return possiblePrimaryClients
      .filter((client) => client.address?.stateCode === StateCode.Ct)
      .reduce((acc, grantor) => {
        return acc.plus(grantor.stateExemptionUsed ?? 0);
      }, ZERO_DECIMAL);
  }, [possiblePrimaryClients]);

  // get the total CT exemption amount for all CT grantors
  const ctExemptionTotalAmount = useMemo(() => {
    return possiblePrimaryClients
      .filter((client) => client.address?.stateCode === StateCode.Ct)
      .reduce((acc, grantor) => {
        return acc.plus(grantor.totalStateExemption ?? ZERO_DECIMAL);
      }, ZERO_DECIMAL);
  }, [possiblePrimaryClients]);

  // Check if any grantor is a CT resident
  const hasCtGrantor = useMemo(() => {
    return possiblePrimaryClients.some(
      (client) => client.address?.stateCode === StateCode.Ct
    );
  }, [possiblePrimaryClients]);

  // get the sections for the lifetime exemption chart
  const lifetimeExemptionSections = useMemo(() => {
    return getLifetimeExemptionSections({
      colors,
      clampNegativeValuesToZero,
      lifetimeExemptionAmountForAllGrantors,
      possiblePrimaryClients,
      lifetimeExclusionUsedTotal,
      groupByGrantor,
      lifetimeExemptionAmountPerGrantor,
      grantorIdToExemptionsMap,
      theme,
    });
  }, [
    clampNegativeValuesToZero,
    colors,
    grantorIdToExemptionsMap,
    groupByGrantor,
    lifetimeExclusionUsedTotal,
    lifetimeExemptionAmountForAllGrantors,
    lifetimeExemptionAmountPerGrantor,
    possiblePrimaryClients,
    theme,
  ]);

  // get the sections for the GST exemption chart
  const gstExemptionSections = useMemo(() => {
    return getGSTExemptionSections({
      colors,
      clampNegativeValuesToZero,
      gstExemptionAmountForAllGrantors,
      possiblePrimaryClients,
      gstExclusionUsedTotal,
      groupByGrantor,
      gstExemptionAmountPerGrantor,
      grantorIdToExemptionsMap,
    });
  }, [
    clampNegativeValuesToZero,
    colors,
    grantorIdToExemptionsMap,
    groupByGrantor,
    gstExclusionUsedTotal,
    gstExemptionAmountForAllGrantors,
    gstExemptionAmountPerGrantor,
    possiblePrimaryClients,
  ]);

  // get the sections for the CT exemption chart
  const ctExemptionSections = useMemo(() => {
    return getCTExemptionSections({
      colors,
      clampNegativeValuesToZero,
      possiblePrimaryClients,
      ctExemptionTotalAmount,
      ctExemptionUsedTotal,
      groupByGrantor,
    });
  }, [
    colors,
    clampNegativeValuesToZero,
    possiblePrimaryClients,
    ctExemptionTotalAmount,
    ctExemptionUsedTotal,
    groupByGrantor,
  ]);

  const allDsue = useMemo(() => {
    return sumDecimalJS(
      possiblePrimaryClients.map((client) => {
        return client.dsue?.amount ?? new Decimal(0);
      })
    );
  }, [possiblePrimaryClients]);

  const availableLifetimeExemption = clampNegativeValuesToZero(
    lifetimeExemptionAmountForAllGrantors
      ?.plus(allDsue)
      .minus(lifetimeExclusionUsedTotal)
  );
  const availableGstExemption = clampNegativeValuesToZero(
    gstExemptionAmountForAllGrantors?.minus(gstExclusionUsedTotal)
  );
  const availableCtExemption = clampNegativeValuesToZero(
    ctExemptionTotalAmount.minus(ctExemptionUsedTotal)
  );

  return {
    lifetimeExemptionSections,
    gstExemptionSections,
    ctExemptionSections,
    availableLifetimeExemption,
    availableGstExemption,
    availableCtExemption,
    hasCtGrantor,
    grantorIdToExemptionsMap,
  };
}
